"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TriangleAlertIcon } from "lucide-react";
import { ClientInfoLayout } from "@/components/backoffice/cat/vehicle-assignment/client-step/client-info-layout";
import { IndividualsSection } from "@/components/backoffice/cat/vehicle-assignment/client-step/client-individuals-section";
import {
  RetailIndividualCreateForm,
  retailIndividualCreateDefaults,
  type RetailIndividualCreateValues,
} from "./retail-individual-create-form";
import { RetailCompanySection } from "./retail-company-section";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import type {
  RetailCompany,
  RetailIndividual,
} from "@/lib/cat/retail-client.types";
import {
  PARTICULIER_SERVER_ERROR_MESSAGE,
  SIRET_VERIFY_INTERNAL_ERROR_MESSAGE,
  isSiretLookupBlocking,
  type ParticulierSearchOutcome,
  type SiretLookupResult,
} from "@/lib/cat/client-step-contracts";
import {
  buildClientDraftSlice,
  buildCompanyDraftSlice,
  individualToEditableWithIdentity,
  isCompanyEditComplete,
  isCompanyRegisteredContactEditComplete,
  isCompanyRegisteredInRefPers,
  isIndividualEditComplete,
} from "@/lib/cat/draft-client-mapper";
import type { EditableCompanyProfile } from "@/lib/cat/company-profile.types";
import type { EditableIndividualWithIdentity } from "@/lib/cat/individual-profile.types";
import {
  MDM_EMAIL_BRAND_LABELS,
  resolveMdmEmailBrandFromKvpsIdentifier,
} from "@/lib/cat/mdm-email-brand";
import {
  buildCompanyProfileFromInsee,
  buildPrefilledFieldLocks,
  emptyPrefilledFieldLocks,
  refPersCompanyToEditableProfile,
  type CompanyPrefilledFieldLocks,
} from "@/lib/cat/mdm-map-company";
import {
  buildParticulierSearchCriteriaFromDraft,
  draftToEditableIndividualWithIdentity,
  isDraftIndividualCreatePath,
  isIndividualPanelActive,
  clientStepPathFromDraft,
  shouldHydrateIndividualForPath,
} from "@/lib/cat/draft-client-hydration";
import { draftToEditableCompanyProfile } from "@/lib/cat/mdm-recap-from-draft";
import {
  useCatParticulierSearchMutation,
  useCatSiretVerifyMutation,
} from "@/lib/queries";
import { saveDraftData } from "@/app/lib/actions/cat/client-step";
import { useVehicleAssignmentStepError } from "@/components/backoffice/cat/vehicle-assignment/vehicle-assignment-step-error";
import { useMdmClientHealthStore } from "@/stores/mdm-client-health-store";
import { useCurrentDraftStore } from "@/stores/selected-vehicle-store";
import { useRetailVehicleAssignmentStore } from "@/stores/mulesoft-vehicle-data-Store";
import { isKvpsEligibleForRentChoice, type Kvps } from "@/lib/kvps";

interface ClientProps {
  currentKvps?: Kvps | null;
  onCanProceedChange?: (canProceed: boolean) => void;
  onBeforeNextChange?: (
    handler: (() => boolean | void | Promise<boolean | void>) | null,
  ) => void;
}

type SaveClientDraftParams = Parameters<typeof saveDraftData>[0];

type RentAnswer = "no" | "yes";
type DirectorAnswer = "no" | "yes";
type ExclusiveKind = "individual" | "company";

function resolveVehicleMdmBrandCode(
  draftKvps: string | null | undefined,
  owningDealerKvps: string | null | undefined,
): string | null {
  return (
    resolveMdmEmailBrandFromKvpsIdentifier(draftKvps) ??
    resolveMdmEmailBrandFromKvpsIdentifier(owningDealerKvps)
  );
}

function RetailClientStep({
  currentKvps,
  onCanProceedChange,
  onBeforeNextChange,
}: ClientProps) {
  const muleSoftVehicleData = useRetailVehicleAssignmentStore(
    (state) => state.muleSoftVehicleData,
  );
  const { showStepError } = useVehicleAssignmentStepError();
  const currentDraft = useCurrentDraftStore((state) => state.currentDraft);
  const setCurrentDraft = useCurrentDraftStore(
    (state) => state.setCurrentDraft,
  );

  const mdmBrandCode = useMemo(
    () =>
      resolveVehicleMdmBrandCode(
        currentDraft?.kvps,
        muleSoftVehicleData?.owningDealerKvps,
      ),
    [currentDraft?.kvps, muleSoftVehicleData?.owningDealerKvps],
  );

  const emailBrandLabel = mdmBrandCode
    ? MDM_EMAIL_BRAND_LABELS[mdmBrandCode]
    : null;

  const showRentChoice = useMemo(
    () => isKvpsEligibleForRentChoice(currentDraft?.kvps, currentKvps),
    [currentDraft?.kvps, currentKvps],
  );

  const [rentSale, setRentSale] = useState<RentAnswer | null>(
    currentDraft
      ? currentDraft.rentSale === true
        ? "yes"
        : currentDraft.rentSale === false
          ? "no"
          : !showRentChoice
            ? "no"
            : null
      : null,
  );
  const [directorSale, setDirectorSale] = useState<DirectorAnswer | null>(
    currentDraft
      ? currentDraft.directorSale === null
        ? null
        : currentDraft.directorSale
          ? "yes"
          : "no"
      : null,
  );
  const [exclusiveKind, setExclusiveKind] = useState<ExclusiveKind | null>(
    currentDraft ? (currentDraft.exclusiveKind as ExclusiveKind) : null,
  );

  const [siretInput, setSiretInput] = useState(currentDraft?.siretInput || "");
  const [siretResult, setSiretResult] = useState<SiretLookupResult | null>(
    null,
  );
  const [validatedCompany, setValidatedCompany] =
    useState<RetailCompany | null>(null);
  const [companyEdit, setCompanyEdit] = useState<EditableCompanyProfile | null>(
    null,
  );
  const [companyPrefilledLocks, setCompanyPrefilledLocks] =
    useState<CompanyPrefilledFieldLocks>(emptyPrefilledFieldLocks);
  const [companyCreateValid, setCompanyCreateValid] = useState(false);

  const [pEmail, setPEmail] = useState("");
  const [pPhone, setPPhone] = useState("");
  const [pLastName, setPLastName] = useState("");
  const [pFirstName, setPFirstName] = useState("");

  const [particulierOutcome, setParticulierOutcome] =
    useState<ParticulierSearchOutcome | null>(null);
  const [selectedIndividualId, setSelectedIndividualId] = useState<
    string | null
  >(null);
  const [ficheEdit, setFicheEdit] =
    useState<EditableIndividualWithIdentity | null>(null);
  const [individualCreateValid, setIndividualCreateValid] = useState(false);
  const [individualCreateValues, setIndividualCreateValues] =
    useState<RetailIndividualCreateValues>(retailIndividualCreateDefaults);
  const [autoOpenCreateForm, setAutoOpenCreateForm] = useState(false);
  const mdmClientHealthMessage = useMdmClientHealthStore(
    (state) => state.message,
  );
  const mdmClientHealthErrorKind = useMdmClientHealthStore(
    (state) => state.errorKind,
  );

  const particulierSearchMutation = useCatParticulierSearchMutation();
  const siretVerifyMutation = useCatSiretVerifyMutation();
  const hydratedIndividualDraftIdRef = useRef<number | null>(null);
  const hydrateIndividualGenerationRef = useRef(0);
  const [isHydratingIndividual, setIsHydratingIndividual] = useState(false);
  const [
    isManualParticulierSearchPending,
    setIsManualParticulierSearchPending,
  ] = useState(false);

  const cancelIndividualHydration = useCallback(() => {
    hydrateIndividualGenerationRef.current += 1;
    setIsHydratingIndividual(false);
    particulierSearchMutation.reset();
    setIsManualParticulierSearchPending(false);
  }, [particulierSearchMutation]);

  const resetClientState = useCallback(() => {
    cancelIndividualHydration();
    setSiretInput("");
    setSiretResult(null);
    setValidatedCompany(null);
    setCompanyEdit(null);
    setCompanyPrefilledLocks(emptyPrefilledFieldLocks());
    setCompanyCreateValid(false);
    setParticulierOutcome(null);
    setSelectedIndividualId(null);
    setFicheEdit(null);
    setIndividualCreateValid(false);
    setIndividualCreateValues(retailIndividualCreateDefaults);
    setAutoOpenCreateForm(false);
    setPEmail("");
    setPPhone("");
    setPLastName("");
    setPFirstName("");
  }, [cancelIndividualHydration]);

  const resetSiretSearch = useCallback(() => {
    setSiretInput("");
    setSiretResult(null);
    setValidatedCompany(null);
    setCompanyEdit(null);
    setCompanyPrefilledLocks(emptyPrefilledFieldLocks());
    setCompanyCreateValid(false);
  }, []);

  const resetParticulierSearch = useCallback(() => {
    cancelIndividualHydration();
    setParticulierOutcome(null);
    setSelectedIndividualId(null);
    setFicheEdit(null);
    setIndividualCreateValid(false);
    setIndividualCreateValues(retailIndividualCreateDefaults);
    setAutoOpenCreateForm(false);
    setPEmail("");
    setPPhone("");
    setPLastName("");
    setPFirstName("");
  }, [cancelIndividualHydration]);

  const selectedIndividual = useMemo((): RetailIndividual | null => {
    if (!selectedIndividualId || !particulierOutcome) return null;
    if (particulierOutcome.kind !== "list") return null;
    return (
      particulierOutcome.individuals.find(
        (i) => i.id === selectedIndividualId,
      ) ?? null
    );
  }, [particulierOutcome, selectedIndividualId]);

  const handleSelectIndividual = useCallback(
    (id: string | null) => {
      setSelectedIndividualId(id);
      if (!id) {
        setFicheEdit(null);
        return;
      }
      if (particulierOutcome?.kind !== "list") {
        setFicheEdit(null);
        return;
      }
      const individual = particulierOutcome.individuals.find(
        (i) => i.id === id,
      );
      if (!individual) {
        setFicheEdit(null);
        return;
      }
      setFicheEdit(individualToEditableWithIdentity(individual));
    },
    [particulierOutcome],
  );

  const syncCompanyEditFromLookup = useCallback(
    (company: RetailCompany, result: SiretLookupResult) => {
      if (result.kind !== "known") {
        setCompanyEdit(null);
        setCompanyPrefilledLocks(emptyPrefilledFieldLocks());
        return;
      }
      if (company.mdmReferentialMissing) {
        const profile = buildCompanyProfileFromInsee({
          siret: company.siret,
          denomination: company.inseeDenomination ?? null,
          ape: company.inseeApe ?? company.ape ?? null,
          addressLine: company.inseeAddressLine ?? null,
          addressFloorApartment: company.inseeAddressFloorApartment ?? null,
          addressBuildingResidence:
            company.inseeAddressBuildingResidence ?? null,
          postBox: company.inseePostBox ?? null,
          postalCode: company.inseePostalCode ?? null,
          city: company.inseeCity ?? null,
        });
        setCompanyEdit(profile);
        setCompanyPrefilledLocks(buildPrefilledFieldLocks(profile));
        return;
      }
      const profile = refPersCompanyToEditableProfile(company);
      setCompanyEdit(profile);
      setCompanyPrefilledLocks(emptyPrefilledFieldLocks());
    },
    [],
  );

  const runSiretVerify = useCallback(
    async (siret: string) => {
      try {
        const result = await siretVerifyMutation.mutateAsync({
          mdmBrandCode,
          siret,
        });
        setSiretResult(result);
        if (result.kind === "known") {
          setValidatedCompany(result.company);
          syncCompanyEditFromLookup(result.company, result);
        } else {
          setValidatedCompany(null);
          setCompanyEdit(null);
          setCompanyPrefilledLocks(emptyPrefilledFieldLocks());
        }
      } catch (error) {
        console.error("[RetailClientStep] SIRET verify", error);
        setValidatedCompany(null);
        setCompanyEdit(null);
        setCompanyPrefilledLocks(emptyPrefilledFieldLocks());
        setSiretResult({
          kind: "internal_error",
          message: SIRET_VERIFY_INTERNAL_ERROR_MESSAGE,
        });
      }
    },
    [mdmBrandCode, siretVerifyMutation, syncCompanyEditFromLookup],
  );

  useEffect(() => {
    const siret = currentDraft?.siretInput?.trim();
    if (!siret || siretResult) return;
    setSiretInput(siret);
    const restoredCompany = draftToEditableCompanyProfile(currentDraft);
    if (restoredCompany) {
      setCompanyEdit(restoredCompany);
      setCompanyPrefilledLocks(emptyPrefilledFieldLocks());
    }
    void runSiretVerify(siret);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once per draft id
  }, [currentDraft?.id]);

  const clientStepPath = useMemo(
    () => ({ rentSale, directorSale, exclusiveKind }),
    [directorSale, exclusiveKind, rentSale],
  );

  const hydrateIndividualFromDraft = useCallback(
    async (draft: NonNullable<typeof currentDraft>) => {
      const generation = hydrateIndividualGenerationRef.current + 1;
      hydrateIndividualGenerationRef.current = generation;
      setIsHydratingIndividual(true);

      const savedClientId = draft.clientId?.trim() || null;
      const criteria = buildParticulierSearchCriteriaFromDraft(draft);
      if (
        !savedClientId &&
        !criteria.lastName &&
        !criteria.email &&
        !criteria.fixedPhone &&
        !criteria.mobilePhone
      ) {
        if (generation === hydrateIndividualGenerationRef.current) {
          setIsHydratingIndividual(false);
        }
        return;
      }

      setPEmail(criteria.email);
      setPLastName(criteria.lastName);
      setPFirstName(criteria.firstName ?? "");
      setPPhone(criteria.mobilePhone || criteria.fixedPhone || "");

      try {
        const outcome = savedClientId
          ? await particulierSearchMutation.mutateAsync({
              context: "retail",
              mdmId: savedClientId,
            })
          : await particulierSearchMutation.mutateAsync({
              context: "retail",
              email: criteria.email,
              firstName: criteria.firstName,
              lastName: criteria.lastName,
              fixedPhone: criteria.fixedPhone || undefined,
              mobilePhone: criteria.mobilePhone || undefined,
            });
        if (generation !== hydrateIndividualGenerationRef.current) return;

        setParticulierOutcome(outcome);

        const savedFiche = draftToEditableIndividualWithIdentity(draft);

        if (outcome.kind !== "list") return;

        if (savedClientId) {
          const found = outcome.individuals.find((i) => i.id === savedClientId);
          if (found) {
            setSelectedIndividualId(savedClientId);
            setFicheEdit(savedFiche ?? individualToEditableWithIdentity(found));
            return;
          }
          setSelectedIndividualId(null);
        }

        if (outcome.individuals.length === 0 && savedFiche) {
          setIndividualCreateValues({
            ...savedFiche,
            birthDate: "",
          });
          setIndividualCreateValid(isIndividualEditComplete(savedFiche));
          setAutoOpenCreateForm(isDraftIndividualCreatePath(draft));
        }
      } catch (error) {
        if (generation !== hydrateIndividualGenerationRef.current) return;
        console.error("[RetailClientStep] particulier search hydrate", error);
        setParticulierOutcome({
          kind: "error",
          message: PARTICULIER_SERVER_ERROR_MESSAGE,
        });
      } finally {
        if (generation === hydrateIndividualGenerationRef.current) {
          setIsHydratingIndividual(false);
        }
      }
    },
    [particulierSearchMutation],
  );

  useEffect(() => {
    if (!currentDraft?.id) return;
    if (hydratedIndividualDraftIdRef.current === currentDraft.id) return;

    const savedPath = clientStepPathFromDraft(currentDraft);
    if (!shouldHydrateIndividualForPath(currentDraft, savedPath)) return;

    hydratedIndividualDraftIdRef.current = currentDraft.id;
    void hydrateIndividualFromDraft(currentDraft);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore once when entering step
  }, [currentDraft?.id]);

  const isRestoringCompany =
    siretVerifyMutation.isPending &&
    Boolean(currentDraft?.siretInput?.trim()) &&
    validatedCompany === null;

  const showDirector = rentSale === "no";
  const showExclusive = rentSale === "no" && directorSale === "no";
  const showCompanyPanel =
    rentSale === "yes" ||
    (rentSale === "no" &&
      (directorSale === "yes" ||
        (directorSale === "no" && exclusiveKind === "company")));
  const showIndividualPanel = isIndividualPanelActive(clientStepPath);
  const showBlock2 = showCompanyPanel || showIndividualPanel;

  const isRestoringIndividual =
    isHydratingIndividual && showIndividualPanel && particulierOutcome === null;

  const handleBeforeNext = useCallback(async () => {
    if (currentDraft === null) {
      showStepError("Aucun brouillon associé à cette affectation.");
      return false;
    }

    const isCreateIndividualPath =
      particulierOutcome?.kind === "list" &&
      particulierOutcome.individuals.length === 0 &&
      individualCreateValid;

    const clientSlice = isCreateIndividualPath
      ? buildClientDraftSlice({
          clientFirstName: individualCreateValues.firstName,
          clientLastName: individualCreateValues.lastName,
          clientId: null,
          fiche: individualCreateValues,
          searchEmail: pEmail,
        })
      : buildClientDraftSlice({
          clientFirstName: ficheEdit?.firstName.trim() ?? "",
          clientLastName: ficheEdit?.lastName.trim() ?? "",
          clientId: selectedIndividualId,
          fiche: ficheEdit,
          searchEmail: pEmail,
        });

    const companySlice = buildCompanyDraftSlice({
      company: validatedCompany,
      companyEdit,
      siretInput,
    });

    const params: SaveClientDraftParams = {
      id: currentDraft.id,
      rentSale: rentSale === null ? null : rentSale === "yes",
      directorSale: directorSale === null ? null : directorSale === "yes",
      exclusiveKind: exclusiveKind,
      ...clientSlice,
      ...companySlice,
    };

    const result = await saveDraftData(params);

    if (!result.success) {
      showStepError(
        result.message ??
          "Une erreur est survenue lors de l'enregistrement du véhicule.",
      );
      return false;
    }

    if (!result.data) {
      showStepError(
        "Aucune donnée client n'a été retournée après l'enregistrement.",
      );
      return false;
    }

    setCurrentDraft(result.data);

    return true;
  }, [
    companyEdit,
    currentDraft,
    directorSale,
    exclusiveKind,
    ficheEdit,
    individualCreateValid,
    individualCreateValues,
    pEmail,
    particulierOutcome,
    rentSale,
    selectedIndividualId,
    setCurrentDraft,
    showStepError,
    siretInput,
    validatedCompany,
  ]);

  useEffect(() => {
    onBeforeNextChange?.(handleBeforeNext);
    return () => onBeforeNextChange?.(null);
  }, [handleBeforeNext, onBeforeNextChange]);

  const ficheComplete = isIndividualEditComplete(ficheEdit);

  const companyPathComplete = useMemo(() => {
    if (!validatedCompany || siretResult?.kind !== "known") return false;
    if (isSiretLookupBlocking(siretResult)) return false;
    if (validatedCompany.mdmReferentialMissing) {
      return companyCreateValid && isCompanyEditComplete(companyEdit);
    }
    if (isCompanyRegisteredInRefPers(validatedCompany)) {
      return isCompanyRegisteredContactEditComplete(companyEdit);
    }
    return false;
  }, [companyCreateValid, companyEdit, siretResult, validatedCompany]);

  const individualPathComplete = useMemo(() => {
    if (!particulierOutcome) return false;
    if (particulierOutcome.kind === "error") return false;
    if (particulierOutcome.kind === "no_internal_collaborator") return false;
    if (particulierOutcome.kind === "no_criteria") return false;
    if (particulierOutcome.kind === "list") {
      if (particulierOutcome.individuals.length === 0) {
        return individualCreateValid;
      }
      if (!selectedIndividualId || !ficheEdit) return false;
      return ficheComplete;
    }
    return false;
  }, [
    ficheComplete,
    ficheEdit,
    individualCreateValid,
    particulierOutcome,
    selectedIndividualId,
  ]);

  const canProceed = useMemo(() => {
    if (rentSale === "yes") return companyPathComplete;
    if (directorSale === null) return false;

    if (directorSale === "yes") {
      return companyPathComplete && individualPathComplete;
    }
    if (exclusiveKind === null) return false;
    if (exclusiveKind === "company") return companyPathComplete;
    return individualPathComplete;
  }, [
    companyPathComplete,
    directorSale,
    exclusiveKind,
    individualPathComplete,
    rentSale,
  ]);

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  const handleVerifySiret = useCallback(async () => {
    await runSiretVerify(siretInput);
  }, [runSiretVerify, siretInput]);

  const handleSiretInputChange = useCallback((value: string) => {
    setSiretInput(value);
    setSiretResult(null);
    setValidatedCompany(null);
    setCompanyEdit(null);
    setCompanyPrefilledLocks(emptyPrefilledFieldLocks());
    setCompanyCreateValid(false);
  }, []);

  const handleIndividualSearch = useCallback(async () => {
    setSelectedIndividualId(null);
    setFicheEdit(null);
    setIsManualParticulierSearchPending(true);
    try {
      const outcome = await particulierSearchMutation.mutateAsync({
        context: "retail",
        email: pEmail.trim(),
        firstName: pFirstName.trim() || undefined,
        lastName: pLastName.trim(),
        phone: pPhone.trim(),
      });
      setParticulierOutcome(outcome);
    } catch (error) {
      console.error("[RetailClientStep] particulier search", error);
      setParticulierOutcome({
        kind: "error",
        message: PARTICULIER_SERVER_ERROR_MESSAGE,
      });
    } finally {
      setIsManualParticulierSearchPending(false);
    }
  }, [pEmail, pFirstName, pLastName, pPhone, particulierSearchMutation]);

  return (
    <ClientInfoLayout>
      <div className="space-y-8">
        <section className="space-y-6">
          {showRentChoice && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Label className="text-sm font-medium">
                  Est-ce une vente RENT ?
                </Label>
              </div>
              <RadioGroup
                className="flex flex-wrap gap-4"
                value={rentSale}
                onValueChange={(v) => {
                  setRentSale(v as RentAnswer);
                  setDirectorSale(null);
                  setExclusiveKind(null);
                  resetClientState();
                }}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="rent-yes" value="yes" />
                  <Label htmlFor="rent-yes">Oui</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="rent-no" value="no" />
                  <Label htmlFor="rent-no">Non</Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {showDirector ? (
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                Est-ce une vente à dirigeant ?
              </Label>
              <RadioGroup
                className="flex flex-wrap gap-4"
                value={directorSale ?? undefined}
                onValueChange={(v) => {
                  setDirectorSale(v as DirectorAnswer);
                  setExclusiveKind(null);
                  resetClientState();
                }}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="dir-yes" value="yes" />
                  <Label htmlFor="dir-yes">Oui</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="dir-no" value="no" />
                  <Label htmlFor="dir-no">Non</Label>
                </div>
              </RadioGroup>
            </div>
          ) : null}

          {showExclusive ? (
            <div className="space-y-3">
              <Label className="text-sm font-medium">Type de client</Label>
              <RadioGroup
                className="flex flex-wrap gap-4"
                value={exclusiveKind ?? undefined}
                onValueChange={(v) => {
                  setExclusiveKind(v as ExclusiveKind);
                  resetClientState();
                }}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="tk-p" value="individual" />
                  <Label htmlFor="tk-p">Particulier</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="tk-s" value="company" />
                  <Label htmlFor="tk-s">Société</Label>
                </div>
              </RadioGroup>
            </div>
          ) : null}
        </section>

        {showBlock2 ? (
          <>
            <div className="space-y-8">
              {mdmClientHealthMessage &&
              mdmClientHealthErrorKind === "functional" ? (
                <Alert
                  variant="destructive"
                  className="border-destructive/50 bg-destructive/5"
                >
                  <TriangleAlertIcon className="text-destructive size-4" />
                  <AlertTitle>Fiche client RefPers</AlertTitle>
                  <AlertDescription>{mdmClientHealthMessage}</AlertDescription>
                </Alert>
              ) : null}
              {showCompanyPanel ? (
                <>
                  <Separator />
                  <RetailCompanySection
                    companyEdit={companyEdit}
                    companyPrefilledLocks={companyPrefilledLocks}
                    emailBrandLabel={emailBrandLabel}
                    isRestoringFromDraft={isRestoringCompany}
                    isVerifying={siretVerifyMutation.isPending}
                    siretInput={siretInput}
                    siretResult={siretResult}
                    validatedCompany={validatedCompany}
                    onCompanyEditChange={setCompanyEdit}
                    onCreateCanProceedChange={setCompanyCreateValid}
                    onResetSearch={resetSiretSearch}
                    onSiretChange={handleSiretInputChange}
                    onVerify={handleVerifySiret}
                  />
                </>
              ) : null}

              {showIndividualPanel ? (
                <>
                  <Separator />
                  <IndividualsSection
                    idPrefix="retail-p"
                    autoOpenCreateForm={autoOpenCreateForm}
                    isRestoringFromDraft={isRestoringIndividual}
                    isSearchLoading={isManualParticulierSearchPending}
                    sectionTitle="Particulier"
                    criteriaLabel="Critères de recherche"
                    emailPlaceholder="Ex: tom.blanc@demo.retail"
                    searchButtonLabel="Rechercher"
                    resultsLabel={(count) =>
                      `${count} résultat(s) — sélectionnez une fiche`
                    }
                    ficheEdit={ficheEdit}
                    onFicheChange={setFicheEdit}
                    onResetSearch={resetParticulierSearch}
                    onSearch={handleIndividualSearch}
                    onSelectIndividual={handleSelectIndividual}
                    outcome={particulierOutcome}
                    pEmail={pEmail}
                    pFirstName={pFirstName}
                    pLastName={pLastName}
                    pPhone={pPhone}
                    selectedIndividual={selectedIndividual}
                    selectedIndividualId={selectedIndividualId}
                    setPEmail={setPEmail}
                    setPFirstName={setPFirstName}
                    setPLastName={setPLastName}
                    setPPhone={setPPhone}
                    creationConfig={{
                      emptyResultsLabel: "Aucun client trouvé",
                      openCreateLabel: "Créer une nouvelle fiche client",
                      closeCreateLabel: "Fermer la création de fiche",
                      onCreateCanProceedChange: setIndividualCreateValid,
                      renderCreateForm: (onCreateCanProceedChange) => (
                        <RetailIndividualCreateForm
                          onCanProceedChange={onCreateCanProceedChange}
                          value={individualCreateValues}
                          onChange={setIndividualCreateValues}
                        />
                      ),
                    }}
                  />
                </>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </ClientInfoLayout>
  );
}

export { RetailClientStep };
